import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  doc,
  onSnapshot,
  collection,
  query,
  orderBy,
  updateDoc,
  increment,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  where,
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "../../backend/firebase";
import { getStorage, ref, getMetadata } from "firebase/storage";
import { ClipLoader } from "react-spinners";
import Papa from "papaparse";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// --- COMPONENTS ---
import CSVGrid from "../../components/csv-grid/CSVGrid";
import DatasetSource from "../../components/DatasetSource";
import DatasetInteraction from "../../components/DatasetInteraction";
import DatasetAccess from "../../components/dataset-access/DatasetAccess";
import DatasetAnalytics from "../../components/dataset-analytics/DatasetAnalytics";
import Collections from "../../components/collections/Collections";
import DatasetUsage from "../dataset-usage/DatasetUsage";

// --- ICONS ---
import {
  FaHeart,
  FaRegHeart,
  FaChevronDown,
  FaChevronUp,
  FaFileArchive,
  FaMagic,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaFolder,
  FaFolderOpen,
  FaTag,
  FaGlobe,
  FaCalendarAlt,
  FaClock,
  FaBalanceScale,
  FaLink,
  FaClipboardList,
  FaExternalLinkAlt,
  FaCheckSquare,
  FaSquare,
} from "react-icons/fa";

import "./DatasetExpand.css";

const LICENSE_TEXTS_FALLBACK = {
  MIT: `MIT License\n\nCopyright (c) [Year] [Author]...`,
  "Apache-2.0": `Apache License\nVersion 2.0, January 2004...`,
};

// --- HELPER: GET FILE TYPE BADGE ---
const getFileTypeBadge = (fileName) => {
  if (!fileName) return <span className="de-badge de-badge-generic">FILE</span>;
  const ext = fileName.split(".").pop().toLowerCase();
  const extLabel = ext.toUpperCase();

  if (["csv", "xls", "xlsx", "tsv"].includes(ext))
    return <span className="de-badge de-badge-data">{extLabel}</span>;

  if (["json", "xml", "js", "py", "html", "css", "cpp"].includes(ext))
    return <span className="de-badge de-badge-code">{extLabel}</span>;

  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext))
    return <span className="de-badge de-badge-img">{extLabel}</span>;

  if (["mp3", "wav", "ogg", "flac", "mp4", "webm", "mov"].includes(ext))
    return <span className="de-badge de-badge-media">{extLabel}</span>;

  if (["pdf", "doc", "docx", "txt", "md"].includes(ext))
    return <span className="de-badge de-badge-doc">{extLabel}</span>;

  return <span className="de-badge de-badge-generic">{extLabel}</span>;
};

// --- HELPER: FORMAT BYTES ---
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 B";
  if (!bytes) return "--";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

// --- RECURSIVE FILE TREE COMPONENT ---
const FileTreeNode = ({
  node,
  level = 0,
  onFileSelect,
  selectedFileId,
  isDisabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [size, setSize] = useState(node.fileData?.size || null);

  useEffect(() => {
    if (node.type === "file" && !size && node.fileData?.fileUrl) {
      const fetchMetadata = async () => {
        try {
          const storage = getStorage();
          const fileRef = ref(storage, node.fileData.fileUrl);
          const metadata = await getMetadata(fileRef);
          setSize(metadata.size);
        } catch (error) {
          setSize(0);
        }
      };
      fetchMetadata();
    }
  }, [node, size]);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (node.type === "folder") {
      setIsOpen(!isOpen);
    } else {
      if (!isDisabled && onFileSelect) {
        onFileSelect(node.fileData);
      }
    }
  };

  const sortedChildren = useMemo(() => {
    if (!node.children) return [];
    return Object.values(node.children).sort((a, b) => {
      if (a.type === b.type) return a.name.localeCompare(b.name);
      return a.type === "folder" ? -1 : 1;
    });
  }, [node.children]);

  const indent = level * 20;

  return (
    <div className="de-tree-node-wrapper">
      <div
        className={`de-tree-row ${node.type} ${
          selectedFileId === node.fileData?.id ? "selected" : ""
        } ${isDisabled ? "disabled" : ""}`}
        style={{ paddingLeft: `${indent + 10}px` }}
        onClick={handleClick}
      >
        <span className="de-tree-icon">
          {node.type === "folder" ? (
            isOpen ? (
              <FaFolderOpen color="#EAB308" />
            ) : (
              <FaFolder color="#EAB308" />
            )
          ) : (
            getFileTypeBadge(node.name)
          )}
        </span>

        <span className="de-tree-name">{node.name}</span>

        {node.type === "file" && (
          <span className="de-tree-meta">{formatBytes(size)}</span>
        )}

        {node.type === "folder" && (
          <span className="de-tree-chevron">
            {isOpen ? (
              <FaChevronDown size={10} />
            ) : (
              <FaChevronRight size={10} />
            )}
          </span>
        )}
      </div>

      {node.type === "folder" && isOpen && (
        <div className="de-tree-children">
          {sortedChildren.map((child) => (
            <FileTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onFileSelect={onFileSelect}
              selectedFileId={selectedFileId}
              isDisabled={isDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- INSTANCE ITEM COMPONENT ---
const InstanceItem = ({
  instance,
  onDelete,
  onSelectFile,
  isOwner,
  versionDisabled,
}) => {
  const [previewData, setPreviewData] = useState([]);
  const [previewHeader, setPreviewHeader] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const dataFile = instance.files?.find(
    (f) => f.fileType === "data" || (f.fileName && f.fileName.endsWith(".csv"))
  );

  useEffect(() => {
    if (versionDisabled) return;
    let isMounted = true;
    const fetchPreview = async () => {
      if (!dataFile?.fileUrl) return;
      setLoadingPreview(true);
      try {
        const proxyUrl =
          "https://calm-charm-production.up.railway.app/proxy?url=" +
          encodeURIComponent(dataFile.fileUrl);
        const response = await fetch(proxyUrl);
        const text = await response.text();
        Papa.parse(text, {
          preview: 5,
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (isMounted) {
              setPreviewHeader(results.meta.fields || []);
              setPreviewData(results.data || []);
              setLoadingPreview(false);
            }
          },
        });
      } catch (err) {
        if (isMounted) setLoadingPreview(false);
      }
    };
    fetchPreview();
    return () => {
      isMounted = false;
    };
  }, [dataFile, versionDisabled]);

  const formatDate = (seconds) => {
    if (!seconds) return "Unknown";
    return new Date(seconds * 1000).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className={`de-instance-card ${versionDisabled ? "disabled" : ""}`}>
      <div className="de-instance-header">
        <div className="de-instance-info">
          <span className="de-instance-id">{instance.instanceId}</span>
          <span className="de-instance-date">
            {formatDate(instance.savedAt?.seconds)}
          </span>
        </div>
        {isOwner && (
          <button
            className="de-instance-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(instance.id);
            }}
            title="Delete Snapshot"
          >
            <FaTrash />
          </button>
        )}
      </div>

      <div className="de-instance-body">
        {versionDisabled ? (
          <div className="de-preview-empty">Preview Disabled</div>
        ) : loadingPreview ? (
          <div className="de-preview-loading">
            <ClipLoader size={15} color="#667eea" />
            <span>Fetching Data...</span>
          </div>
        ) : previewData.length > 0 ? (
          <div className="de-preview-container">
            <table className="de-preview-table">
              <thead>
                <tr>
                  {previewHeader.slice(0, 3).map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                  {previewHeader.length > 3 && <th>...</th>}
                </tr>
              </thead>
              <tbody>
                {previewData.slice(0, 3).map((row, idx) => (
                  <tr key={idx}>
                    {previewHeader.slice(0, 3).map((h, i) => (
                      <td key={i}>{row[h]}</td>
                    ))}
                    {previewHeader.length > 3 && <td>...</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="de-preview-overlay"></div>
          </div>
        ) : (
          <div className="de-preview-empty">No Data Preview Available</div>
        )}
      </div>

      <div className="de-instance-footer">
        <div className="de-footer-label">Files in snapshot:</div>
        <div className="de-file-chips-wrapper">
          {instance.files?.map((f, i) => (
            <div
              key={i}
              className={`de-file-chip ${versionDisabled ? "disabled" : ""}`}
              onClick={() => !versionDisabled && onSelectFile(f)}
              title={f.fileName}
            >
              {getFileTypeBadge(f.fileName)}
              <span className="de-chip-text">{f.fileName}</span>
              {!versionDisabled && <FaEye className="de-chip-action" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- METADATA ITEM COMPONENT ---
const MetadataItem = ({ icon, label, value, isLink, linkUrl, onClick }) => {
  if (!value) return null;

  return (
    <div className="de-meta-item">
      <div className="de-meta-content">
        <span className="de-meta-label">{label}</span>
        {isLink ? (
          <span
            className="de-meta-link"
            onClick={(e) => {
              e.preventDefault();
              if (onClick) onClick();
              else if (linkUrl) window.open(linkUrl, "_blank");
            }}
          >
            {value} <FaExternalLinkAlt size={10} />
          </span>
        ) : (
          <span className="de-meta-value">{value}</span>
        )}
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
const DatasetExpand = () => {
  const [datasetDetails, setDataSetDetails] = useState(null);
  const [prevVersions, setPrevVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState({ open: false, action: null });

  // Updated License Modal State to include 'type' (view vs download)
  const [licenseModal, setLicenseModal] = useState({
    open: false,
    version: null,
    text: "",
    loading: false,
    type: "view",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [collections, setCollections] = useState([]);
  const [likeLoading, setLikeLoading] = useState(false);
  const [root, setRoot] = useState("EXPLORE");
  const [userLiked, setUserLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expandedVersions, setExpandedVersions] = useState(new Set());
  const [instances, setInstances] = useState({});
  const [instancePage, setInstancePage] = useState({});
  const INSTANCES_PER_PAGE = 3;

  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadErrors, setDownloadErrors] = useState({});
  const { datasetid } = useParams();
  const navigate = useNavigate();
  const uid = auth.currentUser?.uid;
  const csvGridRef = useRef(null);

  // --- DATA FETCHING ---
  useEffect(() => {
    const datasetRef = doc(db, "datasets", datasetid);
    const unsub = onSnapshot(datasetRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setDataSetDetails(data);
        setLikeCount(data.likes || 0);
      }
    });
    return () => unsub();
  }, [datasetid]);

  const canExplore =
    datasetDetails?.author === auth.currentUser?.uid ||
    datasetDetails?.visibility === "Public" ||
    (datasetDetails?.visibility === "Private" &&
      datasetDetails?.access_users?.includes(auth.currentUser?.uid));

  useEffect(() => {
    const versionRefs = query(
      collection(db, "datasets", datasetid, "versions"),
      orderBy("publishedOn", "desc")
    );
    const unsub = onSnapshot(versionRefs, async (snapshot) => {
      const allVersions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPrevVersions(allVersions);
      setLoading(false);
    });
    return () => unsub();
  }, [datasetid]);

  useEffect(() => {
    const fetchFiles = async () => {
      const filesQuery = query(
        collection(db, "datasets", datasetid, "files"),
        orderBy("uploadedAt", "desc")
      );
      const unsub = onSnapshot(filesQuery, async (snapshot) => {
        setFiles(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      });
      return () => unsub();
    };
    fetchFiles();
  }, [datasetid]);

  useEffect(() => {
    const fetchInstancesForVersions = async () => {
      if (!prevVersions.length) return;
      const newInstancesMap = {};
      await Promise.all(
        prevVersions.map(async (version) => {
          try {
            const instanceRef = collection(
              db,
              "datasets",
              datasetid,
              "versions",
              version.id,
              "instances"
            );
            const snapshot = await getDocs(instanceRef);
            const versionInstances = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            versionInstances.sort(
              (a, b) => (b.savedAt?.seconds || 0) - (a.savedAt?.seconds || 0)
            );
            if (versionInstances.length > 0)
              newInstancesMap[version.id] = versionInstances;
          } catch (error) {
            console.error(error);
          }
        })
      );
      setInstances(newInstancesMap);
    };
    fetchInstancesForVersions();
  }, [prevVersions, datasetid]);

  useEffect(() => {
    if (auth.currentUser?.uid) {
      const userLikeRef = doc(
        db,
        "datasets",
        datasetid,
        "likes",
        auth.currentUser.uid
      );
      const unsubLike = onSnapshot(userLikeRef, (doc) =>
        setUserLiked(doc.exists())
      );
      const viewRef = doc(
        db,
        "datasets",
        datasetid,
        "Views",
        auth.currentUser.uid
      );
      const checkView = async () => {
        try {
          const docSnap = await getDoc(viewRef);
          if (!docSnap.exists()) {
            await setDoc(viewRef, { viewedOn: new Date().toISOString() });
            await updateDoc(doc(db, "datasets", datasetid), {
              views: increment(1),
            });
          }
        } catch (error) {}
      };
      checkView();
      const fetchCollections = async () => {
        const privateRef = collection(db, "Users", uid, "collections");
        const publicRef = query(
          collection(db, "collections"),
          where("uid", "==", uid)
        );
        const [pSnap, pubSnap] = await Promise.all([
          getDocs(privateRef),
          getDocs(publicRef),
        ]);
        setCollections([
          ...pSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            source: "private",
          })),
          ...pubSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            source: "public",
          })),
        ]);
      };
      fetchCollections();
      return () => unsubLike();
    }
  }, [datasetid, uid]);

  // --- LOGIC ---
  const buildFileTree = (files) => {
    const tree = {};
    files.forEach((file) => {
      const rawPath = file.path || file.fileName;
      const normalizedPath = rawPath.replace(/\\/g, "/");
      const parts = normalizedPath.split("/");
      let currentLevel = tree;
      parts.forEach((part, index) => {
        const isFile = index === parts.length - 1;
        const id = parts.slice(0, index + 1).join("/");
        if (!currentLevel[part]) {
          currentLevel[part] = {
            id: id,
            name: part,
            type: isFile ? "file" : "folder",
            children: {},
            fileData: isFile ? file : null,
          };
        }
        currentLevel = currentLevel[part].children;
      });
    });
    return tree;
  };

  const getLatestAvailableVersion = () =>
    prevVersions.find((version) => !version.isDisabled);
  const isVersionDisabled = (versionId) =>
    prevVersions.find((v) => v.id === versionId)?.isDisabled;

  const groupFilesByVersion = (filesToGroup = files) => {
    const grouped = {};
    filesToGroup.forEach((file) => {
      const version = file.version || "1.0.0";
      if (!grouped[version]) grouped[version] = [];
      grouped[version].push(file);
    });
    const sortedGrouped = {};
    Object.keys(grouped)
      .sort()
      .reverse()
      .forEach((version) => {
        sortedGrouped[version] = grouped[version];
      });
    return sortedGrouped;
  };

  const toggleVersionExpansion = (versionId) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(versionId)) newExpanded.delete(versionId);
    else newExpanded.add(versionId);
    setExpandedVersions(newExpanded);
  };

  const expandAllVersions = () =>
    setExpandedVersions(new Set(Object.keys(groupFilesByVersion())));
  const collapseAllVersions = () => setExpandedVersions(new Set());
  const scrollToCSVGrid = () => {
    if (csvGridRef.current)
      csvGridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleFileSelect = (file) => {
    const normalizedFile = {
      ...file,
      title: file.title || file.fileName || "Untitled",
      id: file.id || `inst_file_${Date.now()}`,
    };
    setSelectedFile(normalizedFile);
    setTimeout(scrollToCSVGrid, 100);
  };

  const handlePreprocess = (versionId, versionFiles) => {
    const csvFiles = versionFiles.filter(
      (f) =>
        f.fileFormat?.toLowerCase() === "csv" ||
        f.fileName?.toLowerCase().endsWith(".csv")
    );
    if (csvFiles.length === 0)
      return alert("No CSV files found in this version to pre-process.");
    navigate(`/canvas/${datasetid}/${versionId}`, {
      state: {
        datasetName: datasetDetails.name,
        files: csvFiles.map((f) => ({
          name: f.fileName || f.title,
          url: f.fileUrl,
          id: f.id,
        })),
      },
    });
  };

  const trackDownload = async (fileId = null, version = null) => {
    if (!auth.currentUser?.uid) return;
    try {
      const downloadData = {
        downloadedAt: new Date().toISOString(),
        userId: auth.currentUser.uid,
        datasetId: datasetid,
        ...(fileId && { fileId }),
        ...(version && { version }),
      };
      await setDoc(
        doc(
          db,
          "Users",
          auth.currentUser.uid,
          "downloads",
          `${datasetid}_${fileId || version}`
        ),
        downloadData
      );
      await setDoc(
        doc(collection(db, "datasets", datasetid, "downloads")),
        downloadData
      );
      await updateDoc(doc(db, "datasets", datasetid), {
        downloads: increment(1),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleLike = async () => {
    try {
      setLikeLoading(true);
      const datasetRef = doc(db, "datasets", datasetid);
      const userLikeRef = doc(
        db,
        "datasets",
        datasetid,
        "likes",
        auth.currentUser.uid
      );
      const userRef = doc(
        db,
        "Users",
        auth.currentUser.uid,
        "likes",
        datasetid
      );
      if (userLiked) {
        await deleteDoc(userLikeRef);
        await updateDoc(datasetRef, { likes: increment(-1) });
        await deleteDoc(userRef);
        setUserLiked(false);
      } else {
        await setDoc(userLikeRef, { likedAt: new Date().toISOString() });
        await updateDoc(datasetRef, { likes: increment(1) });
        await setDoc(userRef, { likedAt: new Date().toISOString() });
        setUserLiked(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLikeLoading(false);
    }
  };

  const downloadFile = async (url, filename, version) => {
    try {
      if (!auth.currentUser?.uid) throw new Error("User not authenticated");
      await trackDownload(null, version);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      alert(e.message);
    }
  };

  // Helper to load license text
  const fetchLicenseText = async () => {
    let text = "License info unavailable.";
    if (datasetDetails.license_doc_url) {
      try {
        const res = await fetch(
          "https://calm-charm-production.up.railway.app/proxy?url=" +
            encodeURIComponent(datasetDetails.license_doc_url)
        );
        text = res.ok ? await res.text() : "Error loading license.";
      } catch (e) {
        text = "Error loading license.";
      }
    } else if (LICENSE_TEXTS_FALLBACK[datasetDetails.license]) {
      text = LICENSE_TEXTS_FALLBACK[datasetDetails.license];
    } else if (datasetDetails.license) {
      text = `License: ${datasetDetails.license}`;
    }
    return text;
  };

  // 1. Open Modal purely for VIEWING the license (Metadata Click)
  const handleViewLicense = async () => {
    setLicenseModal({
      open: true,
      version: null,
      loading: true,
      text: "Loading...",
      type: "view",
    });
    const text = await fetchLicenseText();
    setLicenseModal((prev) => ({ ...prev, loading: false, text: text }));
  };

  // 2. Open Modal for DOWNLOADING (Zip Click)
  const handleZipDownloadRequest = async (version) => {
    setTermsAccepted(false); // Reset checkbox
    setLicenseModal({
      open: true,
      version: version,
      loading: true,
      text: "Loading...",
      type: "download",
    });
    const text = await fetchLicenseText();
    setLicenseModal((prev) => ({ ...prev, loading: false, text: text }));
  };

  const handleLicenseAccept = () => {
    const v = licenseModal.version;
    setLicenseModal({
      open: false,
      version: null,
      text: "",
      loading: false,
      type: "view",
    });
    if (v) downloadVersionAsZip(v);
  };

  const downloadVersionAsZip = async (version) => {
    setDownloadErrors((p) => ({ ...p, [version]: null }));
    setDownloadingZip(true);
    try {
      const zip = new JSZip();
      const vFiles = filesByVersion[version] || [];
      let success = 0;
      for (const f of vFiles) {
        if (!f.fileUrl) continue;
        try {
          const res = await fetch(
            `https://calm-charm-production.up.railway.app/proxy?url=${encodeURIComponent(
              f.fileUrl
            )}`
          );
          if (!res.ok) throw new Error();
          const blob = await res.blob();
          zip.file(f.path || f.fileName, blob);
          success++;
        } catch (e) {}
      }
      if (success === 0) throw new Error("All downloads failed.");
      const content = await zip.generateAsync({ type: "blob" });
      saveAs(content, `${datasetDetails.name}_v${version}.zip`);
      vFiles.forEach((f) => trackDownload(f.id, version));
    } catch (e) {
      alert(e.message);
      setDownloadErrors((p) => ({ ...p, [version]: e.message }));
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleDeleteInstance = async (instanceId, versionId) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try {
      await deleteDoc(
        doc(
          db,
          "datasets",
          datasetid,
          "versions",
          versionId,
          "instances",
          instanceId
        )
      );
      setInstances((prev) => ({
        ...prev,
        [versionId]: prev[versionId].filter((i) => i.id !== instanceId),
      }));
    } catch (e) {
      alert("Delete failed");
    }
  };

  const handleInstancePagination = (versionId, dir) => {
    setInstancePage((prev) => {
      const curr = prev[versionId] || 1;
      return {
        ...prev,
        [versionId]: dir === "next" ? curr + 1 : Math.max(1, curr - 1),
      };
    });
  };

  const handleDeleteVersion = async (vid) => {
    try {
      await updateDoc(doc(db, "datasets", datasetid, "versions", vid), {
        isDisabled: true,
      });
    } catch (e) {}
  };
  const handleEnableVersion = async (vid) => {
    try {
      await updateDoc(doc(db, "datasets", datasetid, "versions", vid), {
        isDisabled: false,
      });
    } catch (e) {}
  };

  if (loading)
    return (
      <div className="de-loading-container">
        <ClipLoader color="#ffffff" size={30} />
      </div>
    );
  if (!datasetDetails)
    return <div className="de-not-found-container">Dataset Not Found</div>;

  const latestAvailableVersion = getLatestAvailableVersion();
  const filesByVersion = groupFilesByVersion();
  const allVersions = Object.keys(filesByVersion);

  return (
    <div className="de-page-container">
      <div style={{ padding: 10 }}>
        {confirm.open && (
          <div className="de-confirm-overlay">
            <div className="de-confirm-dialog">
              <h2>Confirm Action</h2>
              <div className="de-confirm-buttons">
                <button
                  onClick={() => {
                    if (confirm.action) confirm.action();
                    setConfirm({ open: false });
                  }}
                  className="de-confirm-button de-delete"
                >
                  Yes
                </button>
                <button
                  onClick={() => setConfirm({ open: false })}
                  className="de-confirm-button de-cancel"
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {licenseModal.open && (
          <div className="de-confirm-overlay">
            <div className="de-license-dialog">
              <h2>License Agreement</h2>
              <div className="de-license-content-box">
                {licenseModal.loading ? (
                  <ClipLoader color="white" size={20} />
                ) : (
                  licenseModal.text
                )}
              </div>

              {/* Checkbox only appears if type is download */}
              {licenseModal.type === "download" && !licenseModal.loading && (
                <div
                  className="de-license-checkbox-container"
                  onClick={() => setTermsAccepted(!termsAccepted)}
                >
                  <div
                    className={`de-checkbox ${termsAccepted ? "checked" : ""}`}
                  >
                    {termsAccepted && <FaCheckSquare />}
                  </div>
                  <span className="de-checkbox-label">
                    I have read and agree to the license terms.
                  </span>
                </div>
              )}

              <div className="de-confirm-buttons">
                {licenseModal.type === "download" ? (
                  <>
                    <button
                      onClick={handleLicenseAccept}
                      disabled={licenseModal.loading || !termsAccepted}
                      className={`de-confirm-button de-accept ${
                        !termsAccepted ? "disabled" : ""
                      }`}
                    >
                      Accept & Download
                    </button>
                    <button
                      onClick={() => setLicenseModal({ open: false })}
                      className="de-confirm-button de-cancel"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setLicenseModal({ open: false })}
                    className="de-confirm-button de-cancel"
                    style={{ width: "100%" }}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="de-header">
          <div className="de-title-section">
            <div>
              <h1 className="de-title">{datasetDetails.name}</h1>
              <span className="de-dataset-id">{datasetid}</span>
            </div>
            <div className="de-actions">
              {datasetDetails.author === uid && (
                <span
                  className="de-update-link"
                  onClick={() => navigate(`/update/${datasetid}`)}
                >
                  Update
                </span>
              )}
              <div
                onClick={handleLike}
                className={`de-like-button ${userLiked ? "liked" : ""}`}
              >
                {userLiked ? <FaHeart /> : <FaRegHeart />} {likeCount}
              </div>
              {latestAvailableVersion?.fileUrl && canExplore && (
                <div
                  onClick={() =>
                    downloadFile(
                      latestAvailableVersion.fileUrl,
                      datasetDetails.name,
                      latestAvailableVersion.id
                    )
                  }
                  className="de-download-latest"
                >
                  Download Latest
                </div>
              )}
            </div>
          </div>

          <div className="de-description">{datasetDetails.description}</div>

          {/* --- METADATA GRID --- */}
          <div className="de-metadata-grid">
            <MetadataItem
              icon={<FaEye />}
              label="Visibility"
              value={datasetDetails.visibility}
            />
            <MetadataItem
              icon={<FaTag />}
              label="Category"
              value={datasetDetails.category}
            />
            <MetadataItem
              icon={<FaClipboardList />}
              label="Collection Method"
              value={datasetDetails.collectionMethod}
            />
            <MetadataItem
              icon={<FaClock />}
              label="Update Frequency"
              value={datasetDetails.updateFrequency}
            />
            <MetadataItem
              icon={<FaGlobe />}
              label="Geographic Coverage"
              value={datasetDetails.geographicCoverage}
            />
            <MetadataItem
              icon={<FaCalendarAlt />}
              label="Time Period"
              value={datasetDetails.timePeriod}
            />
            <MetadataItem
              icon={<FaBalanceScale />}
              label="License"
              value={datasetDetails.license}
              isLink={true}
              onClick={handleViewLicense}
            />
            <MetadataItem
              icon={<FaLink />}
              label="Source"
              value={
                datasetDetails.source &&
                datasetDetails.source.startsWith("http")
                  ? "Visit Source"
                  : datasetDetails.source
              }
              isLink={
                datasetDetails.source &&
                datasetDetails.source.startsWith("http")
              }
              linkUrl={datasetDetails.source}
            />
          </div>

          {/* --- TABS --- */}
          <div className="de-tabs-nav">
            {/* Logic: If public/owner, show all. If private, only show USAGE and others if allowed. */}
            {[
              "EXPLORE",
              "USAGE",
              "SOURCES",
              "INTERACTIONS",
              "COLLECTIONS",
              "COLLABORATORS",
              "ANALYTICS",
            ].map(
              (tab) =>
                ((tab === "EXPLORE" && canExplore) || tab !== "EXPLORE") && (
                  <span
                    key={tab}
                    onClick={() => setRoot(tab)}
                    className={`de-tab-item ${root === tab ? "active" : ""}`}
                  >
                    {tab.replace("_", " ")}
                  </span>
                )
            )}
          </div>

          {/* --- CONTENT AREAS --- */}

          {root === "EXPLORE" && (
            <div className="de-tab-content">
              <div className="de-version-controls">
                <h3>Files by Version</h3>
                <div className="de-version-buttons">
                  <button
                    onClick={expandAllVersions}
                    className="de-control-btn"
                  >
                    <FaChevronDown /> Expand All
                  </button>
                  <button
                    onClick={collapseAllVersions}
                    className="de-control-btn"
                  >
                    <FaChevronUp /> Collapse All
                  </button>
                </div>
              </div>

              <div className="de-all-versions">
                {allVersions.map((version) => {
                  const isDisabled = isVersionDisabled(version);
                  const vFiles = filesByVersion[version] || [];
                  const vInstances = instances[version] || [];
                  const fileTree = buildFileTree(vFiles);
                  const sortedRootNodes = Object.values(fileTree).sort(
                    (a, b) => {
                      if (a.type === b.type)
                        return a.name.localeCompare(b.name);
                      return a.type === "folder" ? -1 : 1;
                    }
                  );
                  const page = instancePage[version] || 1;
                  const totalP = Math.ceil(
                    vInstances.length / INSTANCES_PER_PAGE
                  );
                  const showInstances = vInstances.slice(
                    (page - 1) * INSTANCES_PER_PAGE,
                    page * INSTANCES_PER_PAGE
                  );

                  return (
                    <div
                      key={version}
                      className={`de-version-section ${
                        isDisabled ? "disabled" : ""
                      }`}
                    >
                      <div
                        className="de-version-header"
                        onClick={() => toggleVersionExpansion(version)}
                      >
                        <span className="de-version-name">
                          Version {version}{" "}
                          {isDisabled && (
                            <span className="de-disabled-badge">Disabled</span>
                          )}
                        </span>
                        <span className="de-version-count">
                          {vFiles.length} Files • {vInstances.length}{" "}
                          Snapshot(s)
                        </span>
                        {expandedVersions.has(version) ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </div>
                      {expandedVersions.has(version) && (
                        <div className="de-version-body">
                          <div className="de-files-tree-container">
                            {sortedRootNodes.map((node) => (
                              <FileTreeNode
                                key={node.id}
                                node={node}
                                onFileSelect={handleFileSelect}
                                selectedFileId={selectedFile?.id}
                                isDisabled={isDisabled}
                              />
                            ))}
                          </div>
                          {vInstances.length > 0 && (
                            <div className="de-instances-container">
                              <h4 className="de-instances-title">
                                Processed Snapshots
                              </h4>
                              <div className="de-instances-grid">
                                {showInstances.map((inst) => (
                                  <InstanceItem
                                    key={inst.id}
                                    instance={inst}
                                    isOwner={inst.lastModifiedBy === uid}
                                    versionDisabled={isDisabled}
                                    onDelete={(id) =>
                                      handleDeleteInstance(id, version)
                                    }
                                    onSelectFile={handleFileSelect}
                                  />
                                ))}
                              </div>
                              {totalP > 1 && (
                                <div className="de-pagination">
                                  <button
                                    disabled={page === 1}
                                    onClick={() =>
                                      handleInstancePagination(version, "prev")
                                    }
                                  >
                                    <FaChevronLeft />
                                  </button>
                                  <span>
                                    {page} / {totalP}
                                  </span>
                                  <button
                                    disabled={page === totalP}
                                    onClick={() =>
                                      handleInstancePagination(version, "next")
                                    }
                                  >
                                    <FaChevronRight />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          <div className="de-version-actions">
                            <button
                              onClick={() => handlePreprocess(version, vFiles)}
                              disabled={isDisabled}
                              className="de-action-btn primary"
                            >
                              Pre-process
                            </button>
                            {vFiles.length > 0 && (
                              <button
                                onClick={() =>
                                  handleZipDownloadRequest(version)
                                }
                                disabled={isDisabled || downloadingZip}
                                className="de-action-btn secondary"
                              >
                                {downloadingZip ? "Downloading..." : "Zip"}
                              </button>
                            )}
                            {datasetDetails.author === uid &&
                              (isDisabled ? (
                                <button
                                  onClick={() => handleEnableVersion(version)}
                                  className="de-action-btn success"
                                >
                                  Enable
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleDeleteVersion(version)}
                                  className="de-action-btn danger"
                                >
                                  Disable
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {selectedFile && (
                <div className="de-csv-wrapper" ref={csvGridRef}>
                  <div className="de-csv-header">
                    <h3>
                      {getFileTypeBadge(selectedFile.fileName)}
                      <span style={{ marginLeft: 10 }}>
                        {selectedFile.title}
                      </span>
                    </h3>
                  </div>
                  <CSVGrid
                    key={selectedFile.id}
                    csvUrl={selectedFile.fileUrl}
                    fileName={selectedFile.fileName}
                    datasetDetails={datasetDetails}
                    onDownloadTrack={() =>
                      trackDownload(selectedFile.id, selectedFile.version)
                    }
                  />
                </div>
              )}
            </div>
          )}

          {/* --- USAGE TAB --- */}
          {root === "USAGE" && (
            <div className="de-tab-content usage-mode">
              <DatasetUsage datasetId={datasetid} />
            </div>
          )}

          {root === "SOURCES" && (
            <div className="de-tab-content">
              <DatasetSource datasetDetails={datasetDetails} />
            </div>
          )}
          {root === "INTERACTIONS" && (
            <div className="de-tab-content">
              <DatasetInteraction datasetDetails={datasetDetails} />
            </div>
          )}
          {root === "COLLECTIONS" && (
            <div className="de-tab-content">
              <Collections
                collections={collections}
                datasetid={datasetid}
                uid={uid}
              />
            </div>
          )}
          {root === "COLLABORATORS" && (
            <div className="de-tab-content">
              <DatasetAccess
                datasetid={datasetid}
                datasetDetails={datasetDetails}
              />
            </div>
          )}
          {root === "ANALYTICS" && (
            <div className="de-tab-content">
              <DatasetAnalytics datasetid={datasetid} />
            </div>
          )}
        </div>
      </div>
      <div style={{ height: 300, backgroundColor: "black" }}></div>
    </div>
  );
};

export default DatasetExpand;
